import { DatePicker, Select, Table } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  getAdmissionConsentList,
  getAdmissionConsentSummary,
  getSignupConsentList,
  getSignupConsentSummary,
} from '../../api/consent.api';
import Button from '../../components/Button';
import DownloadButton from '../../components/DownloadButton';
import ErrorResponse from '../../components/ErrorResponse';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Loader from '../../components/Loader';
import Pagination from '../../components/Pagination';
import SelectCampusMultiChoice from '../../components/SelectCampusMultiChoice';
import TabItem from '../../components/statistics/TabItem';
import TableEmpty from '../../components/common/TableEmpty';
import useAxios from '../../hooks/useAxios';
import config from '../../lib/config';
import { exportAdmissionConsent, exportSignupConsent } from '../../lib/exportConsent';
import { RootState } from '../../store/rootReducer';
import { ADMISSION_CONSENT_ITEMS, CONSENT_CLASS_STATUS_LIST, SIGNUP_CONSENT_ITEMS } from '../../constans';
import {
  IAdmissionConsentListItem,
  IAdmissionConsentSearchParams,
  IConsentSummaryBlock,
  IGetAdmissionConsentList,
  IGetAdmissionConsentSummary,
  IGetSignupConsentList,
  IGetSignupConsentSummary,
  ISignupConsentListItem,
  ISignupConsentSearchParams,
} from '../../models/consent.model';

const { Option } = Select;

/** 이 화면의 실무 목적: 미동의자를 찾아 독려·연락하기 위함 */
enum MainTab {
  SignupSummary,
  SignupList,
  AdmissionSummary,
  AdmissionList,
}

// 다른 통계 화면과 같이 최상위는 Fragment 로 둔다.
// styled.div 로 전체를 감싸면 `.content { height:100%; overflow:hidden }` 의 높이 계산이 끊겨
// 목록 하단(엑셀 버튼·페이징)이 잘린다.
const SummaryCards = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  .card {
    flex: 1;
    padding: 16px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    text-align: center;
  }
  .card .label {
    color: #888;
    font-size: 13px;
  }
  .card .value {
    font-size: 22px;
    font-weight: 700;
    margin-top: 4px;
  }
`;

const Footnote = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #888;
`;

const Ox = styled.span`
  font-weight: 800;
  &.o {
    color: #0a8f5b;
  }
  &.x {
    color: #c0392b;
  }
`;

const renderOx = (value: 'Y' | 'N' | undefined) =>
  value === 'Y' ? <Ox className="o">O</Ox> : <Ox className="x">X</Ox>;

/** 귀속 캠퍼스가 여럿이면 "OO캠퍼스 외 N개" 로 줄인다. */
const renderCampusNames = (value: string) => {
  if (!value) return '-';
  const names = value
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  if (names.length <= 1) return value;
  return `${names[0]} 외 ${names.length - 1}개`;
};

const StatisticsConsent = () => {
  const { t } = useTranslation();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const isHq = auth?.hqYn === 'Y';

  const {
    data: signupSummaryRes,
    isLoading: signupSummaryLoading,
    error: signupSummaryError,
    execute: executeSignupSummary,
  } = useAxios<IGetSignupConsentSummary>();
  const {
    data: signupListRes,
    isLoading: signupListLoading,
    error: signupListError,
    execute: executeSignupList,
  } = useAxios<IGetSignupConsentList>();
  const {
    data: admissionSummaryRes,
    isLoading: admissionSummaryLoading,
    error: admissionSummaryError,
    execute: executeAdmissionSummary,
  } = useAxios<IGetAdmissionConsentSummary>();
  const {
    data: admissionListRes,
    isLoading: admissionListLoading,
    error: admissionListError,
    execute: executeAdmissionList,
  } = useAxios<IGetAdmissionConsentList>();

  const [mainTab, setMainTab] = useState<MainTab>(MainTab.SignupSummary);

  // 검색조건 — 탭에 따라 일부만 노출/사용한다.
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [consentStatus, setConsentStatus] = useState('');
  const [classStatusCodes, setClassStatusCodes] = useState<string[]>(['PL', 'RE']); // 입학동의 탭 전용
  const [missingItemCodes, setMissingItemCodes] = useState<string[]>([]);
  const [studentName, setStudentName] = useState(''); // 입학동의 현황 전용
  const [parentName, setParentName] = useState('');
  const [srtDate, setSrtDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [signupPageNo, setSignupPageNo] = useState(1);
  const [admissionPageNo, setAdmissionPageNo] = useState(1);
  const [signupLastParam, setSignupLastParam] = useState<ISignupConsentSearchParams>({});
  const [admissionLastParam, setAdmissionLastParam] = useState<IAdmissionConsentSearchParams>({});

  const isSignupTab = mainTab === MainTab.SignupSummary || mainTab === MainTab.SignupList;
  // 요약은 캠퍼스(+수강반 상태)만 반영된다. 상태·항목·이름·날짜 조건은 현황(목록) 전용이다.
  const isSummaryTab = mainTab === MainTab.SignupSummary || mainTab === MainTab.AdmissionSummary;
  const targetItems = isSignupTab ? SIGNUP_CONSENT_ITEMS : ADMISSION_CONSENT_ITEMS;
  const resolvedCampusCodes = isHq ? campusCodes : currentCampus ? [currentCampus] : [];

  const buildSignupParams = (pageNo: number, missingCodes: string[]): ISignupConsentSearchParams => {
    const params: ISignupConsentSearchParams = {
      campusCode: resolvedCampusCodes,
      isHq,
      pageNo,
      perPage: config.middlePageSize,
    };
    if (consentStatus) params.consentStatus = consentStatus;
    if (missingCodes.length) params.missingItemCodes = missingCodes;
    if (parentName) params.parentName = parentName;
    if (srtDate) params.srtDate = srtDate;
    if (endDate) params.endDate = endDate;
    return params;
  };

  const buildAdmissionParams = (pageNo: number, missingCodes: string[]): IAdmissionConsentSearchParams => {
    const params: IAdmissionConsentSearchParams = {
      campusCode: resolvedCampusCodes,
      classStatusCodes,
      pageNo,
      perPage: config.middlePageSize,
    };
    if (consentStatus) params.consentStatus = consentStatus;
    if (missingCodes.length) params.missingItemCodes = missingCodes;
    if (studentName) params.studentName = studentName;
    if (parentName) params.parentName = parentName;
    if (srtDate) params.srtDate = srtDate;
    if (endDate) params.endDate = endDate;
    return params;
  };

  // 탭마다 필요한 API 만 호출한다.
  const runSearch = (tab: MainTab, missingCodes: string[]) => {
    if (tab === MainTab.SignupSummary) {
      executeSignupSummary(getSignupConsentSummary(buildSignupParams(1, missingCodes)));
      return;
    }
    if (tab === MainTab.SignupList) {
      const params = buildSignupParams(1, missingCodes);
      setSignupPageNo(1);
      setSignupLastParam(params);
      executeSignupList(getSignupConsentList(params));
      return;
    }
    if (tab === MainTab.AdmissionSummary) {
      executeAdmissionSummary(getAdmissionConsentSummary(buildAdmissionParams(1, missingCodes)));
      return;
    }
    const params = buildAdmissionParams(1, missingCodes);
    setAdmissionPageNo(1);
    setAdmissionLastParam(params);
    executeAdmissionList(getAdmissionConsentList(params));
  };

  const search = () => runSearch(mainTab, missingItemCodes);

  // 첫 진입 시, 그리고 탭 전환 시 해당 탭 기본 조건으로 조회한다.
  // 회원가입 <-> 입학동의는 동의 항목 구성이 달라 미동의 항목 선택값을 비우고 조회한다.
  useEffect(() => {
    if (!auth) return;
    setMissingItemCodes([]);
    runSearch(mainTab, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, mainTab]);

  const onChangeSignupPage = (newPage: number) => {
    setSignupPageNo(newPage);
    const params = { ...signupLastParam, pageNo: newPage };
    setSignupLastParam(params);
    executeSignupList(getSignupConsentList(params));
  };

  const onChangeAdmissionPage = (newPage: number) => {
    setAdmissionPageNo(newPage);
    const params = { ...admissionLastParam, pageNo: newPage };
    setAdmissionLastParam(params);
    executeAdmissionList(getAdmissionConsentList(params));
  };

  const onDownloadSignupExcel = async () => {
    const { data } = await getSignupConsentList({ ...signupLastParam, pageNo: 1, perPage: 999999 });
    exportSignupConsent(data.data);
  };

  const onDownloadAdmissionExcel = async () => {
    const { data } = await getAdmissionConsentList({
      ...admissionLastParam,
      pageNo: 1,
      perPage: 999999,
    });
    exportAdmissionConsent(data.data);
  };

  const renderSummaryBlock = (
    denominatorLabel: string,
    block: IConsentSummaryBlock | undefined,
    items: typeof SIGNUP_CONSENT_ITEMS,
    unassignedFootnote?: string,
  ) => {
    if (!block) return <TableEmpty text={t('statistics.appday.text1')} />;
    return (
      <div className="col-frame">
        <SummaryCards>
          <div className="card">
            <div className="label">대상 {denominatorLabel}</div>
            <div className="value">{block.targetCount.toLocaleString()}</div>
          </div>
          <div className="card">
            <div className="label">완료</div>
            <div className="value">{block.completeCount.toLocaleString()}</div>
          </div>
          <div className="card">
            <div className="label">미완료</div>
            <div className="value">{block.incompleteCount.toLocaleString()}</div>
          </div>
          <div className="card">
            <div className="label">동의율</div>
            <div className="value">{block.consentRate}%</div>
          </div>
        </SummaryCards>
        <div className="comm-table__area">
          <Table
            rowKey="campusCode"
            pagination={false}
            dataSource={block.campusRows}
            columns={[
              {
                title: '캠퍼스',
                dataIndex: 'campusName',
                key: 'campusName',
                align: 'center',
              },
              {
                title: `대상 ${denominatorLabel}`,
                dataIndex: 'targetCount',
                key: 'targetCount',
                align: 'center',
              },
              ...items.map((item) => ({
                title: item.required ? item.label : `${item.label} (선택)`,
                key: item.code,
                align: 'center' as const,
                render: (_value: any, record: any) => {
                  const count = record.itemCounts[item.code] ?? 0;
                  const rate = record.targetCount
                    ? Math.round((count / record.targetCount) * 1000) / 10
                    : 0;
                  return `${count} (${rate}%)`;
                },
              })),
            ]}
          />
        </div>
        <Footnote>
          ※ 위 완료/미완료·동의율은 (필수) 항목 기준입니다. (선택) 항목은 참고용입니다.
          {unassignedFootnote ? ` ${unassignedFootnote}` : ''}
        </Footnote>
        <Footnote>
          ※ 요약은 캠퍼스{isSignupTab ? '' : '·수강반 상태'} 조건만 반영합니다. 동의 상태·미동의 항목·이름·날짜 조건은 현황 탭에서 사용하세요.
        </Footnote>
      </div>
    );
  };

  const signupColumns: any[] = [
    {
      title: '캠퍼스(귀속)',
      dataIndex: 'attributedCampusNames',
      key: 'attributedCampusNames',
      align: 'center',
      render: (value: string) => renderCampusNames(value),
    },
    { title: '학부모명', dataIndex: 'parentName', key: 'parentName', align: 'center' },
    { title: '자녀 수', dataIndex: 'childCount', key: 'childCount', align: 'center' },
    ...SIGNUP_CONSENT_ITEMS.map((item) => ({
      title: item.label,
      key: item.code,
      align: 'center',
      render: (_value: any, record: ISignupConsentListItem) =>
        renderOx(record.signupItemAgreeYn[item.code]),
    })),
    { title: '미동의 수(필수)', dataIndex: 'signupMissingCount', key: 'signupMissingCount', align: 'center' },
    {
      title: '최종 동의일시',
      dataIndex: 'signupLastConsentDttm',
      key: 'signupLastConsentDttm',
      align: 'center',
      render: (value: string | null) => (value ? moment(value).format('YYYY.MM.DD HH:mm') : '-'),
    },
  ];

  const admissionColumns: any[] = [
    { title: '캠퍼스', dataIndex: 'campusName', key: 'campusName', align: 'center' },
    { title: '학부모명', dataIndex: 'parentName', key: 'parentName', align: 'center' },
    { title: '학생명', dataIndex: 'studentName', key: 'studentName', align: 'center' },
    { title: '학년', dataIndex: 'gradeName', key: 'gradeName', align: 'center' },
    // 보류 (2026-09-22) — 필요해지면 되살린다. API 는 값을 계속 내려준다.
    // { title: '수강반', dataIndex: 'className', key: 'className', align: 'center' },
    // { title: '수강반상태', dataIndex: 'classStatusName', key: 'classStatusName', align: 'center' },
    ...ADMISSION_CONSENT_ITEMS.map((item) => ({
      title: item.label,
      key: item.code,
      align: 'center',
      render: (_value: any, record: IAdmissionConsentListItem) =>
        renderOx(record.admissionItemAgreeYn[item.code]),
    })),
    { title: '미동의 수(필수)', dataIndex: 'admissionMissingCount', key: 'admissionMissingCount', align: 'center' },
    {
      title: '최종 동의일시',
      dataIndex: 'admissionLastConsentDttm',
      key: 'admissionLastConsentDttm',
      align: 'center',
      render: (value: string | null) => (value ? moment(value).format('YYYY.MM.DD HH:mm') : '-'),
    },
    // 보류 (2026-09-22) — 필요해지면 되살린다. API 는 값을 계속 내려준다.
    // {
    //   title: (
    //     <span title="학부모 계정의 회원가입 추가 동의(필수 항목) 완료 여부">학부모 회원가입 동의</span>
    //   ),
    //   dataIndex: 'parentSignupCompleteYn',
    //   key: 'parentSignupCompleteYn',
    //   align: 'center',
    //   render: (value: 'Y' | 'N') => renderOx(value),
    // },
  ];

  const signupList = signupListRes?.data ?? [];
  const signupTotal = signupListRes?.total ?? 0;
  const admissionList = admissionListRes?.data ?? [];
  const admissionTotal = admissionListRes?.total ?? 0;

  return (
    <>
      <SimpleHeader
        iconFile="icon-statistics-big.svg"
        parentMenu={t('common.menu.statistics')}
        currentMenu="학생 동의 현황"
        onlyTitle={true}
      />
      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label>
              <div>
                <SelectCampusMultiChoice
                  hasHq={false}
                  normalOnly
                  disabled={!isHq}
                  value={resolvedCampusCodes}
                  onChange={setCampusCodes}
                />
              </div>
            </li>
            {!isSignupTab && (
              <li>
                <label>수강반 상태</label>
                <Select
                  mode="multiple"
                  className="w-210"
                  value={classStatusCodes}
                  onChange={setClassStatusCodes}
                  placeholder={t('common.text.all')}
                >
                  {CONSENT_CLASS_STATUS_LIST.map((item) => (
                    <Option key={item.code} value={item.code}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              </li>
            )}
            {!isSummaryTab && (
              <>
                <li>
                  <label>동의 상태</label>
                  <Select className="w-150" value={consentStatus} onChange={setConsentStatus}>
                    <Option value="">{t('common.text.all')}</Option>
                    <Option value="INCOMPLETE">미동의만</Option>
                    <Option value="COMPLETE">완료</Option>
                  </Select>
                </li>
                <li>
                  <label>미동의 항목</label>
                  <Select
                    mode="multiple"
                    className="w-210"
                    value={missingItemCodes}
                    onChange={setMissingItemCodes}
                    placeholder={t('common.text.all')}
                  >
                    {targetItems.map((item) => (
                      <Option key={item.code} value={item.code}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                </li>
                {mainTab === MainTab.AdmissionList && (
                  <li>
                    <input
                      type="text"
                      className="form-control w-150"
                      placeholder="학생명"
                      value={studentName}
                      onChange={(e) => setStudentName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') search();
                      }}
                    />
                  </li>
                )}
                <li>
                  <input
                    type="text"
                    className="form-control w-150"
                    placeholder="학부모명"
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') search();
                    }}
                  />
                </li>
                <li>
                  <label>동의일시</label>
                  <div className="date-picker__area">
                    <DatePicker
                      value={srtDate ? moment(srtDate, 'YYYYMMDD') : undefined}
                      onChange={(e) => {
                        if (e === null) setSrtDate('');
                        else setSrtDate(moment(e).format('YYYYMMDD'));
                      }}
                      format={['YYYY-MM-DD', 'YYYYMMDD']}
                    />
                  </div>
                  ~
                  <div className="date-picker__area">
                    <DatePicker
                      value={endDate ? moment(endDate, 'YYYYMMDD') : undefined}
                      onChange={(e) => {
                        if (e === null) setEndDate('');
                        else setEndDate(moment(e).format('YYYYMMDD'));
                      }}
                      format={['YYYY-MM-DD', 'YYYYMMDD']}
                    />
                  </div>
                </li>
              </>
            )}
            <li>
              <div className="comm-btn__area">
                <Button className="btn-basic small black" onClick={search} text={t('common.button.search')} />
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="content scrollbar">
        {(signupSummaryLoading || signupListLoading || admissionSummaryLoading || admissionListLoading) && (
          <Loader />
        )}
        <ErrorResponse
          error={
            isSignupTab
              ? signupSummaryError || signupListError
              : admissionSummaryError || admissionListError
          }
        />

        <div className="comm-tab__line">
          <ul>
            <TabItem
              currentContent={mainTab}
              onSetContent={setMainTab}
              tabContent={MainTab.SignupSummary}
              tabLabel="회원가입 동의 요약"
            />
            <TabItem
              currentContent={mainTab}
              onSetContent={setMainTab}
              tabContent={MainTab.SignupList}
              tabLabel="회원가입 동의 현황"
            />
            <TabItem
              currentContent={mainTab}
              onSetContent={setMainTab}
              tabContent={MainTab.AdmissionSummary}
              tabLabel="입학동의 요약"
            />
            <TabItem
              currentContent={mainTab}
              onSetContent={setMainTab}
              tabContent={MainTab.AdmissionList}
              tabLabel="입학동의 현황"
            />
          </ul>
        </div>

        {mainTab === MainTab.SignupSummary &&
          renderSummaryBlock(
            '학부모',
            signupSummaryRes?.data,
            SIGNUP_CONSENT_ITEMS,
            '캠퍼스 귀속은 학생 캠퍼스 이력의 마지막 캠퍼스 기준입니다(현재 재원 여부와 무관). "본사" 행과 "미배정"(자녀가 아예 없는 학부모) 행은 본사 계정에서만 표시됩니다. 자녀가 서로 다른 캠퍼스에 재학한 이력이 있으면 해당 학부모는 각 캠퍼스에 중복 집계됩니다.',
          )}

        {mainTab === MainTab.SignupList && (
          <div className="col-frame">
            <div className="comm-bx__between">
              <div className="comm-sch_result">
                {t('common.text.count')}: <b>{signupTotal}</b>
                {t('common.text.unit')}
              </div>
            </div>
            {signupList.length === 0 && !signupListLoading ? (
              <TableEmpty text={t('statistics.appday.text1')} />
            ) : (
              <div className="comm-table__area">
                <Table
                  columns={signupColumns}
                  dataSource={signupList}
                  pagination={false}
                  rowKey="parentCode"
                />
              </div>
            )}
            <Footnote>
              ※ 학부모 동의는 계정 단위이므로 자녀가 여럿이어도 1행으로만 표시됩니다.
            </Footnote>
            <div className="comm-bx__between">
              <DownloadButton onClick={onDownloadSignupExcel} />
              <div className="common-paging center">
                <Pagination
                  count={signupTotal}
                  page={signupPageNo}
                  setPage={onChangeSignupPage}
                  perPage={config.middlePageSize}
                />
              </div>
            </div>
          </div>
        )}

        {mainTab === MainTab.AdmissionSummary &&
          renderSummaryBlock('학생', admissionSummaryRes?.data, ADMISSION_CONSENT_ITEMS)}

        {mainTab === MainTab.AdmissionList && (
          <div className="col-frame">
            <div className="comm-bx__between">
              <div className="comm-sch_result">
                {t('common.text.count')}: <b>{admissionTotal}</b>
                {t('common.text.unit')}
              </div>
            </div>
            {admissionList.length === 0 && !admissionListLoading ? (
              <TableEmpty text={t('statistics.appday.text1')} />
            ) : (
              <div className="comm-table__area">
                <Table
                  columns={admissionColumns}
                  dataSource={admissionList}
                  pagination={false}
                  rowKey="studentCode"
                />
              </div>
            )}
            <div className="comm-bx__between">
              <DownloadButton onClick={onDownloadAdmissionExcel} />
              <div className="common-paging center">
                <Pagination
                  count={admissionTotal}
                  page={admissionPageNo}
                  setPage={onChangeAdmissionPage}
                  perPage={config.middlePageSize}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default StatisticsConsent;
